import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef, Component } from 'react';
import { ChevronDownIcon, PlusIcon, ChevronLeftIcon } from 'lucide-react';
interface PaymentMethodsFiltersProps {
  paymentMethodCategories: string[];
  onPaymentMethodCategoriesChange: (categories: string[]) => void;
  paymentMethods: string[];
  onPaymentMethodsChange: (methods: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  fundingSourceTypes: string[];
  onFundingSourceTypesChange: (types: string[]) => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  inputMethods: string[];
  onInputMethodsChange: (methods: string[]) => void;
  interactionTypes: string[];
  onInteractionTypesChange: (types: string[]) => void;
  networkTokenStatuses: string[];
  onNetworkTokenStatusesChange: (statuses: string[]) => void;
}
export function PaymentMethodsFilters({
  paymentMethodCategories,
  onPaymentMethodCategoriesChange,
  paymentMethods,
  onPaymentMethodsChange,
  cardBrands,
  onCardBrandsChange,
  fundingSourceTypes,
  onFundingSourceTypesChange,
  cardTypes,
  onCardTypesChange,
  inputMethods,
  onInputMethodsChange,
  interactionTypes,
  onInteractionTypesChange,
  networkTokenStatuses,
  onNetworkTokenStatusesChange
}: PaymentMethodsFiltersProps) {
  const {
    t
  } = useTranslation("analytics");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showInputMethodDropdown, setShowInputMethodDropdown] = useState(false);
  const [showInteractionTypeDropdown, setShowInteractionTypeDropdown] = useState(false);
  const [showNetworkTokenDropdown, setShowNetworkTokenDropdown] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const methodRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const inputMethodRef = useRef<HTMLDivElement>(null);
  const interactionTypeRef = useRef<HTMLDivElement>(null);
  const networkTokenRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (methodRef.current && !methodRef.current.contains(event.target as Node)) {
        setShowMethodDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (inputMethodRef.current && !inputMethodRef.current.contains(event.target as Node)) {
        setShowInputMethodDropdown(false);
      }
      if (interactionTypeRef.current && !interactionTypeRef.current.contains(event.target as Node)) {
        setShowInteractionTypeDropdown(false);
      }
      if (networkTokenRef.current && !networkTokenRef.current.contains(event.target as Node)) {
        setShowNetworkTokenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <>
      {/* Main Filters - Same row as date/granularity */}
      <div className="relative" ref={categoryRef} data-id="element-2007" data-mp="2jfgdlqabi7tm">
        <button onClick={() => setShowCategoryDropdown(!showCategoryDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2008" data-mp="2je54day3s7v4">
          
          <span data-id="element-2009" data-mp="2fju2p00ss4zp">{t("components.AcceptanceFilters.payment_method_category")}</span>
          {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2010" data-mp="2al922zhmvjaq">
              {paymentMethodCategories.length}
            </span>}
          <ChevronDownIcon size={16} data-id="element-2011" data-mp="1it1mm2dv6cxx" />
        </button>

        {showCategoryDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_category")} options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
        onPaymentMethodCategoriesChange(selected);
        setShowCategoryDropdown(false);
      }} onBack={null} data-id="element-2012" data-mp="1iv4hazpyou8z" />}
      </div>

      <div className="relative" ref={methodRef} data-id="element-2013" data-mp="338n43c06v51p">
        <button onClick={() => setShowMethodDropdown(!showMethodDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2014" data-mp="p0m5corpn11d">
          
          <span data-id="element-2015" data-mp="g1qx7753vgvf">{t("components.AcceptanceFilters.payment_method")}</span>
          {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2016" data-mp="l029doivi3gt">
              {paymentMethods.length}
            </span>}
          <ChevronDownIcon size={16} data-id="element-2017" data-mp="1cqgfuzok4dig" />
        </button>

        {showMethodDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_payment_method")} options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
        onPaymentMethodsChange(selected);
        setShowMethodDropdown(false);
      }} onBack={null} data-id="element-2018" data-mp="1csj3jd28luk0" />}
      </div>

      {/* Secondary Filters - Separate row with smaller buttons */}
      <div className="w-full flex flex-wrap items-center gap-2 mt-3" data-id="element-2019" data-mp="26a5xqef419an">
        <div className="relative" ref={cardBrandRef} data-id="element-2020" data-mp="21bbghgwsjssn">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2021" data-mp="219qljq6ke7u9">
            
            <PlusIcon size={14} data-id="element-2022" data-mp="1ncbrg85d9bvy" />
            <span data-id="element-2023" data-mp="1nclfhq6up8z0">{t("components.AcceptanceFilters.card_brand")}</span>
            {cardBrands.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2024" data-mp="1ikbmu2ecput1">
                {cardBrands.length}
              </span>}
          </button>

          {showCardBrandDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_card_brand")} options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-2025" data-mp="18o8h6mkaje1c" />}
        </div>

        <div className="relative" ref={fundingSourceRef} data-id="element-2026" data-mp="3f9lbon0lytak">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2027" data-mp="3k9racn5apg25">
            
            <PlusIcon size={14} data-id="element-2028" data-mp="2rn6htjd32xx0" />
            <span data-id="element-2029" data-mp="2wkofuepkbsq8">{t("components.AcceptanceFilters.funding_source_type")}</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2030" data-mp="31k1dluhz2k7l">
                {fundingSourceTypes.length}
              </span>}
          </button>

          {showFundingSourceDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_funding_source")} options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          onFundingSourceTypesChange(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-2031" data-mp="28vlyl7yuc1w9" />}
        </div>

        <div className="relative" ref={cardTypeRef} data-id="element-2032" data-mp="12a4jsv8u5dfe">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2033" data-mp="1g8cailfa3ii4">
            
            <PlusIcon size={14} data-id="element-2034" data-mp="1q3w1quiwdt0j" />
            <span data-id="element-2035" data-mp="1l6vwgltvguah">{t("components.AcceptanceFilters.card_type")}</span>
            {cardTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2036" data-mp="1yxsoobjvz9h0">
                {cardTypes.length}
              </span>}
          </button>

          {showCardTypeDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_card_type")} options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-2037" data-mp="28w877q4ulaew" />}
        </div>

        <div className="relative" ref={inputMethodRef} data-id="element-2038" data-mp="2sia25141e2zh">
          <button onClick={() => setShowInputMethodDropdown(!showInputMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2039" data-mp="2sfnul1h5bnh9">
            
            <PlusIcon size={14} data-id="element-2040" data-mp="24rw8j65szw2s" />
            <span data-id="element-2041" data-mp="24quuqtxekvhd">{t("components.PaymentMethodsFilters.input_method")}</span>
            {inputMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2042" data-mp="29kpcvsxd12ec">
                {inputMethods.length}
              </span>}
          </button>

          {showInputMethodDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_input_method")} options={['In-person (card present)', 'Online (card-not-present)', 'Apple Pay', 'Google Pay']} selectedOptions={inputMethods} onApply={selected => {
          onInputMethodsChange(selected);
          setShowInputMethodDropdown(false);
        }} onBack={null} data-id="element-2043" data-mp="1ztm7wg1b839s" />}
        </div>

        <div className="relative" ref={interactionTypeRef} data-id="element-2044" data-mp="1h0op3fxtx7rj">
          <button onClick={() => setShowInteractionTypeDropdown(!showInteractionTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2045" data-mp="130dvi554xf2o">
            
            <PlusIcon size={14} data-id="element-2046" data-mp="1uslcj6ojythl" />
            <span data-id="element-2047" data-mp="1utmorehb1m0w">{t("components.AcceptanceFilters.interaction_type")}</span>
            {interactionTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2048" data-mp="1lyyl1srdoce7">
                {interactionTypes.length}
              </span>}
          </button>

          {showInteractionTypeDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_interaction_type")} options={['One time', 'Saved card', 'Recurring initial', 'Recurring subsequent', 'Unscheduled']} selectedOptions={interactionTypes} onApply={selected => {
          onInteractionTypesChange(selected);
          setShowInteractionTypeDropdown(false);
        }} onBack={null} data-id="element-2049" data-mp="2dqefg8z6f3ez" />}
        </div>

        <div className="relative" ref={networkTokenRef} data-id="element-2050" data-mp="grp7fuadh1ea">
          <button onClick={() => setShowNetworkTokenDropdown(!showNetworkTokenDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2051" data-mp="gr5oqbcwe8z7">
            
            <PlusIcon size={14} data-id="element-2052" data-mp="3f9cglc04oej3" />
            <span data-id="element-2053" data-mp="3fae2ycmlgng4">{t("components.PaymentMethodsFilters.network_token_status")}</span>
            {networkTokenStatuses.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2054" data-mp="3u86hp2kbh7t9">
                {networkTokenStatuses.length}
              </span>}
          </button>

          {showNetworkTokenDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_network_token_status")} options={['Non-tokenized card', 'Network token']} selectedOptions={networkTokenStatuses} onApply={selected => {
          onNetworkTokenStatusesChange(selected);
          setShowNetworkTokenDropdown(false);
        }} onBack={null} data-id="element-2055" data-mp="19ejiy1p7oqfm" />}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const {
    t
  } = useTranslation("analytics");
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-2056" data-mp="2scv40ogv8lct">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-2057" data-mp="2ob9r534c072m">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-2058" data-mp="2o95d3scpxrom" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-2059" data-mp="2jaltvzpietzy">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-2060" data-mp="2ecaihkjkgbfm">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-2061" data-mp="29e8y8waem62k">
        
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2062" data-mp="24mh7olr900cf" />
        
            <span className="text-base text-gray-900" data-id="element-2063" data-mp="1vnvoqphv6jb4">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-2064" data-mp="1qm6g1gi46hq7">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-2065" data-mp="1looh8ixt9ivx">
        
          {t("components.content.apply")}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-2066" data-mp="3qrtvil90f9kt">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-2067" data-mp="3c02zimoqdtm1">
      {content}
    </div>;
}